import { Zap } from "lucide-react";
import { QueryStatus, useQuery } from "@tanstack/react-query";

import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, message, formatSnakeCase } from "@/lib/utils";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";
import type { GrowthMomentumData } from "@/schema/brand-os/ai-consumer-research";

function buildBarBounds(data: GrowthMomentumData) {
  const items = data.channels
    .filter((c): c is typeof c & { growth_percentage: number } => c.growth_percentage !== null)
    .map((c) => ({
      platform: c.channel,
      value: c.growth_percentage,
    }));

  const numericValues = items.map((i) => i.value);
  const maxPositive = Math.max(...numericValues.filter((value) => value >= 0), 1);
  const maxNegative = Math.max(...numericValues.filter((value) => value < 0).map((value) => Math.abs(value)), 1);

  return { items, maxPositive, maxNegative };
}

function GrowthMomentumComponent({ status, data }: { status: QueryStatus; data?: GrowthMomentumData }) {
  switch (status) {
    case "pending":
      return <GrowthMomentumSkeleton />;
    case "error":
      return <LockedContent message={message} className="min-h-64" />;
    case "success":
      return data ? <GrowthMomentumContent data={data} /> : <LockedContent message={message} className="min-h-64" />;
  }
}

export function GrowthMomentum() {
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.GrowthMomentum());

  return (
    <Card className="rounded-2xl bg-card shadow-none w-full gap-3">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="flex size-9 items-center justify-center rounded-lg bg-blue-100">
            <Zap className="size-5 text-blue-600" />
          </div>
          <CardTitle className="text-base font-semibold">Growth Momentum</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <GrowthMomentumComponent status={status} data={data} />
      </CardContent>
    </Card>
  );
}

function GrowthMomentumContent({ data }: { data: GrowthMomentumData }) {
  const { items, maxPositive, maxNegative } = buildBarBounds(data);

  return (
    <ul className="space-y-3 max-h-[420px] overflow-auto scrollbar-hidden pr-1">
      {items.map(({ platform, value }) => {
        const isPositive = value >= 0;
        const magnitude = Math.abs(value);
        const widthPercent = isPositive ? (magnitude / maxPositive) * 100 : (magnitude / maxNegative) * 100;
        return (
          <li key={platform} className="flex items-center gap-3">
            <span className="text-sm text-foreground w-28 shrink-0">{formatSnakeCase(platform)}</span>
            <div className="flex-1 min-w-0 h-2 rounded-full bg-muted overflow-hidden">
              <div className={cn("h-full rounded-full transition-all", isPositive ? "bg-success" : "bg-destructive")} style={{ width: `${Math.min(widthPercent, 100)}%` }} />
            </div>
            <span className={cn("text-sm font-medium tabular-nums w-16 text-right shrink-0", isPositive ? "text-success" : "text-destructive")}>{`${isPositive ? "+" : ""}${value} %`}</span>
          </li>
        );
      })}
    </ul>
  );
}

function GrowthMomentumSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-5 w-full rounded" />
      ))}
    </div>
  );
}
